import * as React from 'react';
import Text from '../../shared/Text';
import { ComplexityError, minLength } from '@c42/password-complexity';
import { Box, Typography, useTheme } from '@mui/material';
import { CheckCircle, CircleOutline, CloseCircle } from 'mdi-material-ui';

interface PasswordComplexityProps {
  errors: ComplexityError[];
}

export const PasswordComplexity: React.FC<PasswordComplexityProps> = (props) => {
  const { errors } = props;

  const getComplexityFulfilled = (condition: ComplexityError): Condition => {
    if (errors.indexOf(ComplexityError.EMPTY) >= 0) {
      return Condition.EMPTY;
    }
    if (errors.indexOf(condition) >= 0) {
      return Condition.ERROR;
    }
    return Condition.SUCCESS;
  };

  return (
    <div data-test="password-complexity">
      <Box mt={2} mb={1}>
        <Typography variant="h5">{Text.get('_validate_password_header')}:</Typography>
      </Box>
      <PasswordComplexityRule
        condition={getComplexityFulfilled(ComplexityError.LENGTH)}
        data-test="complexity-length"
        text={Text.get('_validate_password_minlength', { MinLength: minLength.toString() })}
      />
      <PasswordComplexityRule
        condition={getComplexityFulfilled(ComplexityError.NUMBER)}
        data-test="complexity-number"
        text={Text.get('_validate_password_number')}
      />
      <PasswordComplexityRule
        condition={getComplexityFulfilled(ComplexityError.LOWER)}
        data-test="complexity-lower"
        text={Text.get('_validate_password_lower')}
      />
      <PasswordComplexityRule
        condition={getComplexityFulfilled(ComplexityError.UPPER)}
        data-test="complexity-upper"
        text={Text.get('_validate_password_upper')}
      />
      <PasswordComplexityRule
        condition={getComplexityFulfilled(ComplexityError.WHITESPACE)}
        data-test="complexity-whitespace"
        text={Text.get('_validate_password_whitespace')}
      />
      <PasswordComplexityRule
        condition={getComplexityFulfilled(ComplexityError.REPEATING)}
        data-test="complexity-repeating"
        text={Text.get('_validate_password_repeating')}
      />
      <PasswordComplexityRule
        condition={getComplexityFulfilled(ComplexityError.DICTIONARY)}
        data-test="complexity-dictionary"
        text={Text.get('_validate_password_dictionary')}
      />
    </div>
  );
};

enum Condition {
  ERROR,
  EMPTY,
  SUCCESS,
}

type RuleProps = {
  condition: Condition;
  text: string;
};

const PasswordComplexityRule: React.FC<RuleProps> = ({ condition, text, ...props }) => {
  const theme = useTheme();

  const getIcon = () => {
    switch (condition) {
      case Condition.ERROR:
        return <CloseCircle color="error" data-test="condition-error" fontSize="small" />;

      case Condition.EMPTY:
        return <CircleOutline data-test="condition-empty" fontSize="small" />;

      default:
        return <CheckCircle color="success" data-test="condition-success" fontSize="small" />;
    }
  };

  return (
    <Box display="flex" gap={1} ml={1} {...props}>
      {getIcon()}
      <Typography color={condition === Condition.ERROR ? theme.palette.error.main : undefined}>
        {text}
      </Typography>
    </Box>
  );
};
